/** Supplier Master List seed data (Demo Mode only). Ringfenced companion to the real
 * Procurement Suppliers screen (src/pages/Procurement/Suppliers) - see index.tsx, which
 * swaps in the SupplierMasterList table only when isDemoModeEnabled() is true, leaving the
 * production supplier list/API untouched. Purely mock/UI-only data, no backend calls. */

import { SupplierApprovalStatus } from './supplierGovernanceData';

export type SupplierOperationalStatus = 'Active' | 'OnHold' | 'Inactive' | 'Onboarding';
export type SupplierCoverageType = 'National' | 'Regional' | 'SingleSite';
export type SupplierCertificationStatus = 'Valid' | 'ExpiringSoon' | 'Expired' | 'None';

export interface SupplierMasterListEntry {
  supplierId: number;
  name: string;
  category: string;
  approvalStatus: SupplierApprovalStatus;
  operationalStatus: SupplierOperationalStatus;
  coverage: SupplierCoverageType;
  assignedSites: string[];
  activeCatalogues: number;
  certificationStatus: SupplierCertificationStatus;
  openExceptions: number;
  lastReviewed: string;
}

export const SUPPLIER_CATEGORIES = [
  'Fresh Produce',
  'Dairy',
  'Meat & Poultry',
  'Bakery',
  'Beverages',
  'Packaging',
  'Cleaning & Hygiene',
] as const;

export const ASSIGNED_SITE_OPTIONS = ['0101', '0102', '0145', '0203', '0310', 'All Sites'] as const;

export const supplierMasterList: SupplierMasterListEntry[] = [
  {
    supplierId: 1,
    name: 'Millbrook Bakers Ltd',
    category: 'Bakery',
    approvalStatus: SupplierApprovalStatus.Approved,
    operationalStatus: 'Active',
    coverage: 'National',
    assignedSites: ['All Sites'],
    activeCatalogues: 3,
    certificationStatus: 'ExpiringSoon',
    openExceptions: 0,
    lastReviewed: '2026-06-01',
  },
  {
    supplierId: 2,
    name: 'Riverside Dairy Co-op',
    category: 'Dairy',
    approvalStatus: SupplierApprovalStatus.PendingReview,
    operationalStatus: 'Onboarding',
    coverage: 'Regional',
    assignedSites: ['0101'],
    activeCatalogues: 0,
    certificationStatus: 'Expired',
    openExceptions: 2,
    lastReviewed: '2026-06-20',
  },
  {
    supplierId: 3,
    name: 'Kent Farm Produce',
    category: 'Fresh Produce',
    approvalStatus: SupplierApprovalStatus.Approved,
    operationalStatus: 'Active',
    coverage: 'Regional',
    assignedSites: ['0101', '0102', '0145'],
    activeCatalogues: 2,
    certificationStatus: 'Valid',
    openExceptions: 0,
    lastReviewed: '2026-05-15',
  },
  {
    supplierId: 4,
    name: 'Coastal Meats & Poultry',
    category: 'Meat & Poultry',
    approvalStatus: SupplierApprovalStatus.Suspended,
    operationalStatus: 'OnHold',
    coverage: 'SingleSite',
    assignedSites: ['0203'],
    activeCatalogues: 1,
    certificationStatus: 'Expired',
    openExceptions: 3,
    lastReviewed: '2026-04-02',
  },
  {
    supplierId: 5,
    name: 'Northgate Beverages',
    category: 'Beverages',
    approvalStatus: SupplierApprovalStatus.Approved,
    operationalStatus: 'Active',
    coverage: 'National',
    assignedSites: ['All Sites'],
    activeCatalogues: 4,
    certificationStatus: 'Valid',
    openExceptions: 0,
    lastReviewed: '2026-06-28',
  },
  {
    supplierId: 6,
    name: 'PurePack Packaging Solutions',
    category: 'Packaging',
    approvalStatus: SupplierApprovalStatus.AutoApprovedLowVolume,
    operationalStatus: 'Active',
    coverage: 'SingleSite',
    assignedSites: ['0310'],
    activeCatalogues: 1,
    certificationStatus: 'None',
    openExceptions: 0,
    lastReviewed: '2026-03-11',
  },
  {
    supplierId: 7,
    name: 'ShineClean Hygiene Supplies',
    category: 'Cleaning & Hygiene',
    approvalStatus: SupplierApprovalStatus.Rejected,
    operationalStatus: 'Inactive',
    coverage: 'Regional',
    assignedSites: [],
    activeCatalogues: 0,
    certificationStatus: 'None',
    openExceptions: 1,
    lastReviewed: '2026-01-22',
  },
  {
    supplierId: 8,
    name: 'Highland Dairy Fresh',
    category: 'Dairy',
    approvalStatus: SupplierApprovalStatus.Approved,
    operationalStatus: 'Active',
    coverage: 'Regional',
    assignedSites: ['0102', '0145'],
    activeCatalogues: 2,
    certificationStatus: 'Valid',
    openExceptions: 0,
    lastReviewed: '2026-06-10',
  },
  {
    supplierId: 9,
    name: 'Orchard Fresh Fruit & Veg',
    category: 'Fresh Produce',
    approvalStatus: SupplierApprovalStatus.PendingReview,
    operationalStatus: 'Onboarding',
    coverage: 'SingleSite',
    assignedSites: ['0101'],
    activeCatalogues: 0,
    certificationStatus: 'ExpiringSoon',
    openExceptions: 1,
    lastReviewed: '2026-06-25',
  },
  {
    supplierId: 10,
    name: 'Summit Meats Wholesale',
    category: 'Meat & Poultry',
    approvalStatus: SupplierApprovalStatus.Approved,
    operationalStatus: 'Active',
    coverage: 'National',
    assignedSites: ['All Sites'],
    activeCatalogues: 3,
    certificationStatus: 'Valid',
    openExceptions: 0,
    lastReviewed: '2026-05-30',
  },
];
